import { createServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { ALL_DATA_TYPE_SLUGS } from "@/lib/vehicle-data";
import { rotatedWindow } from "@/lib/link-rotation";

import {
  scoreConditionalDifferentiators,
  type ConditionalDimensionRow,
  type ConditionalSafetyRow,
  type ConditionalEvRow,
} from "@/lib/index-policy";

/**
 * Server functions for querying vehicle data from the database.
 * All content is public read-only.
 */

export interface VehicleRow {
  id: string;
  year: number;
  trim: string | null;
  transmission: string | null;
  drive: string | null;
  cylinders: string | null;
  displacement: string | null;
  fuel_type: string | null;
  fuel_type_detail: string | null;
  vehicle_class: string | null;
  engine_desc: string | null;
  mpg_city: number | null;
  mpg_hwy: number | null;
  mpg_combined: number | null;
  annual_fuel_cost: number | null;
  co2_tailpipe_gpm: number | null;
  fuel_economy_score: number | null;
  ghg_score: number | null;
  atv_type: string | null;
  ev_range_miles: number | null;
  ev_range_city: number | null;
  ev_range_hwy: number | null;
  ev_motor: string | null;
  charge_120v_hours: number | null;
  charge_240v_hours: number | null;
  phev_blended: boolean | null;
  source: string;
  confidence: string;
  verified_at: string;
}

export interface MakeRow {
  id: string;
  name: string;
  slug: string;
}

export interface ModelRow {
  id: string;
  make_id: string;
  name: string;
  slug: string;
}

export interface ModelWithCount extends ModelRow {
  vehicle_count: number;
  year_min: number;
  year_max: number;
  /** Real per-model facts used for the make-hub cards (no placeholders). */
  mpg_best: number | null;
  fuel_types: string[];
  body_class: string | null;
  ev_range_best: number | null;
}

export interface MakeWithCount extends MakeRow {
  model_count: number;
}

/** Manufacturer spec row (towing / tire / bolt pattern). */
export interface SpecRow {
  id: string;
  year: number;
  trim: string | null;
  spec_type: string;
  towing_capacity_lbs: number | null;
  towing_config: string | null;
  payload_capacity_lbs: number | null;
  curb_weight_lbs: number | null;
  gcwr_lbs: number | null;
  front_tire_size: string | null;
  rear_tire_size: string | null;
  spare_tire_size: string | null;
  wheel_size: string | null;
  tire_pressure_front_psi: number | null;
  tire_pressure_rear_psi: number | null;
  bolt_pattern: string | null;
  center_bore_mm: number | null;
  lug_nut_torque_ftlb: number | null;
  thread_size: string | null;
  bed_config: string | null;
  bed_length_in: number | null;
  bed_width_in: number | null;
  bed_width_between_wheelwells_in: number | null;
  bed_depth_in: number | null;
  bed_volume_cuft: number | null;
  fuel_tank_gal: number | null;
  fuel_tank_gal_optional: number | null;
  notes: string | null;
  source: string;
  source_url: string | null;
  confidence: string;
  verified_at: string;
}

const SPEC_COLUMNS =
  "id, year, trim, spec_type, towing_capacity_lbs, towing_config, payload_capacity_lbs, curb_weight_lbs, gcwr_lbs, front_tire_size, rear_tire_size, spare_tire_size, wheel_size, tire_pressure_front_psi, tire_pressure_rear_psi, bolt_pattern, center_bore_mm, lug_nut_torque_ftlb, thread_size, bed_config, bed_length_in, bed_width_in, bed_width_between_wheelwells_in, bed_depth_in, bed_volume_cuft, fuel_tank_gal, fuel_tank_gal_optional, notes, source, source_url, confidence, verified_at";

/** NHTSA Canadian Vehicle Specifications dimension row. */
export interface DimensionRow {
  id: string;
  year: number;
  variant_label: string | null;
  length_in: number | null;
  width_in: number | null;
  height_in: number | null;
  wheelbase_in: number | null;
  curb_weight_lbs: number | null;
  track_front_in: number | null;
  track_rear_in: number | null;
  weight_distribution: string | null;
  source: string;
  source_url: string | null;
  confidence: string;
  verified_at: string;
}

const DIMENSION_COLUMNS =
  "id, year, variant_label, length_in, width_in, height_in, wheelbase_in, curb_weight_lbs, track_front_in, track_rear_in, weight_distribution, source, source_url, confidence, verified_at";

/** NHTSA safety rating row. */
export interface SafetyRow {
  id: string;
  year: number;
  variant_label: string;
  overall_rating: string | null;
  overall_front_rating: string | null;
  front_driver_rating: string | null;
  front_passenger_rating: string | null;
  overall_side_rating: string | null;
  side_driver_rating: string | null;
  side_passenger_rating: string | null;
  side_pole_rating: string | null;
  rollover_rating: string | null;
  rollover_possibility: number | null;
  nhtsa_complaints_count: number | null;
  recalls_count: number | null;
  investigation_count: number | null;
  has_esc: boolean | null;
  has_forward_collision_warning: boolean | null;
  has_lane_departure_warning: boolean | null;
  source: string;
  source_url: string | null;
  confidence: string;
  verified_at: string;
}

const SAFETY_COLUMNS =
  "id, year, variant_label, overall_rating, overall_front_rating, front_driver_rating, front_passenger_rating, overall_side_rating, side_driver_rating, side_passenger_rating, side_pole_rating, rollover_rating, rollover_possibility, nhtsa_complaints_count, recalls_count, investigation_count, has_esc, has_forward_collision_warning, has_lane_departure_warning, source, source_url, confidence, verified_at";

const VEHICLE_COLUMNS =
  "id, year, trim, transmission, drive, cylinders, displacement, fuel_type, fuel_type_detail, vehicle_class, engine_desc, mpg_city, mpg_hwy, mpg_combined, annual_fuel_cost, co2_tailpipe_gpm, fuel_economy_score, ghg_score, atv_type, ev_range_miles, ev_range_city, ev_range_hwy, ev_motor, charge_120v_hours, charge_240v_hours, phev_blended, source, confidence, verified_at";

/** Get all makes that have at least one model with vehicle data. */
export const getMakes = createServerFn({ method: "GET" }).handler(async () => {
  const { data, error } = await supabase
    .from("makes")
    .select(
      `
      id, name, slug,
      models!inner ( id )
    `,
    )
    .order("name");

  if (error) {
    console.error("getMakes error:", error);
    return { makes: [] as MakeWithCount[] };
  }

  const makes: MakeWithCount[] = (data ?? []).map((m) => ({
    id: m.id,
    name: m.name,
    slug: m.slug,
    model_count: m.models?.length ?? 0,
  }));

  return { makes };
});

/** Get all models for a specific make. */
export const getModelsForMake = createServerFn({ method: "GET" })
  .inputValidator((data: { makeSlug: string }) => data)
  .handler(async ({ data }) => {
    const { data: makeData, error: makeError } = await supabase
      .from("makes")
      .select("id, name, slug")
      .eq("slug", data.makeSlug)
      .single();

    if (makeError || !makeData) {
      return { make: null, models: [] as ModelWithCount[] };
    }

    const { data: models, error } = await supabase
      .from("models")
      .select("id, make_id, name, slug")
      .eq("make_id", makeData.id)
      .order("name");

    if (error) {
      console.error("getModelsForMake error:", error);
      return { make: makeData, models: [] as ModelWithCount[] };
    }

    // Get vehicle counts and year ranges for each model
    const modelIds = (models ?? []).map((m) => m.id);
    if (modelIds.length === 0) {
      return { make: makeData, models: [] as ModelWithCount[] };
    }

    const { data: vehicleStats } = await supabase
      .from("vehicles")
      .select("model_id, year, mpg_combined, fuel_type, vehicle_class, ev_range_miles")
      .in("model_id", modelIds);

    interface Agg {
      count: number;
      minYear: number;
      maxYear: number;
      mpgBest: number | null;
      evBest: number | null;
      fuels: Map<string, number>;
      classes: Map<string, number>;
    }
    const statsMap = new Map<string, Agg>();
    for (const v of vehicleStats ?? []) {
      let a = statsMap.get(v.model_id);
      if (!a) {
        a = {
          count: 0,
          minYear: v.year,
          maxYear: v.year,
          mpgBest: null,
          evBest: null,
          fuels: new Map(),
          classes: new Map(),
        };
        statsMap.set(v.model_id, a);
      }
      a.count++;
      a.minYear = Math.min(a.minYear, v.year);
      a.maxYear = Math.max(a.maxYear, v.year);
      if (typeof v.mpg_combined === "number" && v.mpg_combined > 0) {
        a.mpgBest = a.mpgBest === null ? v.mpg_combined : Math.max(a.mpgBest, v.mpg_combined);
      }
      if (typeof v.ev_range_miles === "number" && v.ev_range_miles > 0) {
        a.evBest = a.evBest === null ? v.ev_range_miles : Math.max(a.evBest, v.ev_range_miles);
      }
      if (v.fuel_type) a.fuels.set(v.fuel_type, (a.fuels.get(v.fuel_type) ?? 0) + 1);
      if (v.vehicle_class) a.classes.set(v.vehicle_class, (a.classes.get(v.vehicle_class) ?? 0) + 1);
    }
    const topKeys = (m: Map<string, number>, n: number) =>
      [...m.entries()].sort((a, b) => b[1] - a[1]).slice(0, n).map(([k]) => k);

    const modelsWithCounts: ModelWithCount[] = (models ?? [])
      .filter((m) => statsMap.has(m.id))
      .map((m) => {
        const stats = statsMap.get(m.id)!;
        return {
          id: m.id,
          make_id: m.make_id,
          name: m.name,
          slug: m.slug,
          vehicle_count: stats.count,
          year_min: stats.minYear,
          year_max: stats.maxYear,
          mpg_best: stats.mpgBest,
          fuel_types: topKeys(stats.fuels, 2),
          body_class: topKeys(stats.classes, 1)[0] ?? null,
          ev_range_best: stats.evBest,
        };
      });

    return { make: makeData, models: modelsWithCounts };
  });

/** Get a specific make by slug. */
export const getMake = createServerFn({ method: "GET" })
  .inputValidator((data: { slug: string }) => data)
  .handler(async ({ data }) => {
    const { data: make, error } = await supabase
      .from("makes")
      .select("id, name, slug")
      .eq("slug", data.slug)
      .single();

    if (error || !make) {
      return { make: null };
    }

    return { make: make as MakeRow };
  });

/** Get a specific model by make slug + model slug. */
export const getModel = createServerFn({ method: "GET" })
  .inputValidator((data: { makeSlug: string; modelSlug: string }) => data)
  .handler(async ({ data }) => {
    const { data: result, error } = await supabase
      .from("models")
      .select(
        `
        id, name, slug,
        makes!inner ( id, name, slug )
      `,
      )
      .eq("slug", data.modelSlug)
      .eq("makes.slug", data.makeSlug)
      .single();

    if (error || !result) {
      return { model: null, make: null };
    }

    const make = result.makes as unknown as MakeRow;
    const model: ModelRow = {
      id: result.id,
      make_id: make.id,
      name: result.name,
      slug: result.slug,
    };

    return { model, make };
  });

/** Shared implementation: fetch make, model, and all vehicles. */
async function fetchModelWithVehicles(makeSlug: string, modelSlug: string) {
  const { data: result, error } = await supabase
    .from("models")
    .select(
      `
      id, name, slug,
      makes!inner ( id, name, slug )
    `,
    )
    .eq("slug", modelSlug)
    .eq("makes.slug", makeSlug)
    .single();

  if (error || !result) {
    return { model: null, make: null, vehicles: [] as VehicleRow[] };
  }

  const make = result.makes as unknown as MakeRow;
  const model: ModelRow = {
    id: result.id,
    make_id: make.id,
    name: result.name,
    slug: result.slug,
  };

  const { data: vehicles, error: vError } = await supabase
    .from("vehicles")
    .select(VEHICLE_COLUMNS)
    .eq("model_id", model.id)
    .order("year", { ascending: false })
    .order("trim", { ascending: true });

  if (vError) {
    console.error("fetchModelWithVehicles error:", vError);
    return { model, make, vehicles: [] as VehicleRow[] };
  }

  return { model, make, vehicles: (vehicles ?? []) as unknown as VehicleRow[] };
}

/**
 * NHTSA-sourced extras for a model: dimensions and safety ratings.
 * Returned together so the page loader makes one round trip, and so the
 * cross-link chips know which conditional pages actually have data.
 */
export const getNhtsaExtrasForModel = createServerFn({ method: "GET" })
  .inputValidator((data: { modelId: string }) => data)
  .handler(async ({ data }) => {
    const [dimRes, safetyRes] = await Promise.all([
      supabase
        .from("vehicle_dimensions")
        .select(DIMENSION_COLUMNS)
        .eq("model_id", data.modelId)
        .order("year", { ascending: false }),
      supabase
        .from("safety_ratings")
        .select(SAFETY_COLUMNS)
        .eq("model_id", data.modelId)
        .order("year", { ascending: false }),
    ]);

    if (dimRes.error) console.error("dimensions query error:", dimRes.error);
    if (safetyRes.error) console.error("safety query error:", safetyRes.error);

    return {
      dimensions: (dimRes.data ?? []) as unknown as DimensionRow[],
      safety: (safetyRes.data ?? []) as unknown as SafetyRow[],
    };
  });

/** One auto-generated attribute page plus the data density behind it. */
export interface ConditionalPage {
  path: string;
  makeSlug: string;
  modelSlug: string;
  dataType: string;
  rowCount: number;
  latestYear: number | null;
  /** Model-specific facts on the page; used by the sitemap to match the route's robots tag. */
  differentiators: number;
}

/** Every /{make}/{model}/{dataType} page backed by NHTSA or EV data. */
export const getConditionalPageUrls = createServerFn({ method: "GET" }).handler(
  async () => {
    const modelRef = "year, models!inner ( slug, makes!inner ( slug ) )";
    const pages = new Map<
      string,
      ConditionalPage & { rawRows: Array<ConditionalDimensionRow | ConditionalSafetyRow | ConditionalEvRow> }
    >();
    const add = (
      rows: unknown[] | null,
      dataType: string,
      filter?: (r: unknown) => boolean,
    ) => {
      for (const r of rows ?? []) {
        if (filter && !filter(r)) continue;
        const row = r as {
          year: number | null;
          models: { slug: string; makes: { slug: string } } | null;
        };
        const m = row.models;
        if (!m) continue;
        const path = `/${m.makes.slug}/${m.slug}/${dataType}`;
        const existing = pages.get(path);
        if (existing) {
          existing.rowCount += 1;
          existing.rawRows.push(r as ConditionalDimensionRow | ConditionalSafetyRow | ConditionalEvRow);
          if (row.year != null && (existing.latestYear == null || row.year > existing.latestYear)) {
            existing.latestYear = row.year;
          }
        } else {
          pages.set(path, {
            path,
            makeSlug: m.makes.slug,
            modelSlug: m.slug,
            dataType,
            rowCount: 1,
            latestYear: row.year ?? null,
            differentiators: 0,
            rawRows: [r as ConditionalDimensionRow | ConditionalSafetyRow | ConditionalEvRow],
          });
        }
      }
    };

    // PostgREST caps a response at 1,000 rows — page through each source table.
    const PAGE = 1000;
    const pageThrough = async (
      build: (from: number, to: number) => PromiseLike<{ data: unknown[] | null }>,
      apply: (rows: unknown[] | null) => void,
    ) => {
      for (let from = 0; ; from += PAGE) {
        const { data } = await build(from, from + PAGE - 1);
        apply(data ?? []);
        if (!data || data.length < PAGE) break;
      }
    };

    await Promise.all([
      pageThrough(
        (from, to) =>
          supabase
            .from("vehicle_dimensions")
            .select(`length_in, width_in, height_in, wheelbase_in, curb_weight_lbs, ${modelRef}`)
            .order("id")
            .range(from, to),
        (rows) => {
          add(rows, "dimensions");
          add(
            rows,
            "curb-weight",
            (r) => (r as { curb_weight_lbs: number | null }).curb_weight_lbs != null,
          );
        },
      ),
      pageThrough(
        (from, to) =>
          supabase
            .from("safety_ratings")
            .select(
              `overall_rating, overall_front_rating, overall_side_rating, rollover_rating, rollover_possibility, recalls_count, nhtsa_complaints_count, investigation_count, has_forward_collision_warning, has_lane_departure_warning, ${modelRef}`,
            )
            .order("id")
            .range(from, to),
        (rows) => add(rows, "safety-ratings"),
      ),
      pageThrough(
        (from, to) =>
          supabase
            .from("vehicles")
            .select(
              `ev_range_miles, ev_range_city, ev_range_hwy, ev_motor, charge_120v_hours, charge_240v_hours, mpg_combined, trim, ${modelRef}`,
            )
            .not("ev_range_miles", "is", null)
            .order("id")
            .range(from, to),
        (rows) => add(rows, "ev-range"),
      ),
    ]);

    const list = Array.from(pages.values()).map((p) => ({
      ...p,
      differentiators: scoreConditionalDifferentiators(p.dataType, p.rawRows),
    }));
    return { urls: list.map((p) => p.path), pages: list };
  },
);

/** Fetch manufacturer specs of one type for a model. */
export const getSpecsForModel = createServerFn({ method: "GET" })
  .inputValidator((data: { makeSlug: string; modelSlug: string; specType: string }) => data)
  .handler(async ({ data }) => {
    const { data: result } = await supabase
      .from("models")
      .select("id, name, slug, makes!inner ( id, name, slug )")
      .eq("slug", data.modelSlug)
      .eq("makes.slug", data.makeSlug)
      .single();

    if (!result) {
      return {
        model: null,
        make: null,
        specs: [] as SpecRow[],
        allSpecs: [] as SpecRow[],
        availableSpecTypes: [] as string[],
      };
    }

    const make = result.makes as unknown as MakeRow;
    const model: ModelRow = {
      id: result.id,
      make_id: make.id,
      name: result.name,
      slug: result.slug,
    };

    const { data: allSpecs, error } = await supabase
      .from("vehicle_specs")
      .select(SPEC_COLUMNS)
      .eq("model_id", model.id)
      .order("year", { ascending: false })
      .order("trim", { ascending: true });

    if (error) {
      console.error("getSpecsForModel error:", error);
      return {
        model,
        make,
        specs: [] as SpecRow[],
        allSpecs: [] as SpecRow[],
        availableSpecTypes: [] as string[],
      };
    }

    const rows = (allSpecs ?? []) as unknown as SpecRow[];
    return {
      model,
      make,
      specs: rows.filter((s) => s.spec_type === data.specType),
      // Model hubs for EPA-less vehicles render these directly; hubs with EPA
      // rows drop them in the loader so they never reach the payload.
      allSpecs: rows,
      availableSpecTypes: Array.from(new Set(rows.map((s) => s.spec_type))),
    };
  });


/** Which manufacturer spec types exist for a model (for cross-link chips). */
export const getAvailableSpecTypes = createServerFn({ method: "GET" })
  .inputValidator((data: { modelId: string }) => data)
  .handler(async ({ data }) => {
    const { data: rows } = await supabase
      .from("vehicle_specs")
      .select("spec_type")
      .eq("model_id", data.modelId);
    return { specTypes: Array.from(new Set((rows ?? []).map((r) => r.spec_type))) };
  });

/** Every /{make}/{model}/{specType} page that has verified data. */
export const getSpecPageUrls = createServerFn({ method: "GET" }).handler(async () => {
  const { data: rows, error } = await supabase
    .from("vehicle_specs")
    .select("spec_type, models!inner ( slug, makes!inner ( slug ) )")
    .limit(50000);

  if (error) {
    console.error("getSpecPageUrls error:", error);
    return { urls: [] as string[] };
  }

  const urls = new Set<string>();
  for (const r of rows ?? []) {
    const model = r.models as unknown as { slug: string; makes: { slug: string } };
    urls.add(`/${model.makes.slug}/${model.slug}/${r.spec_type}`);
  }
  return { urls: Array.from(urls) };
});

/** Get all vehicle data for a specific model, grouped by year. */
export const getVehiclesForModel = createServerFn({ method: "GET" })
  .inputValidator((data: { makeSlug: string; modelSlug: string }) => data)
  .handler(async ({ data }) => fetchModelWithVehicles(data.makeSlug, data.modelSlug));

/** Get all vehicles for a make + model + data type page (same data, filtered view). */
export const getVehiclesForPage = createServerFn({ method: "GET" })
  .inputValidator(
    (data: { makeSlug: string; modelSlug: string; dataType: string }) => data,
  )
  .handler(async ({ data }) => fetchModelWithVehicles(data.makeSlug, data.modelSlug));

/** Get all public vehicle page URLs for sitemap generation. */
export interface RelatedLinks {
  siblingModels: { name: string; slug: string }[];
  otherMakes: { name: string; slug: string }[];
}

/** Get related internal links (sibling models + other makes) for a page. */
export const getRelatedLinks = createServerFn({ method: "GET" })
  .inputValidator((data: { makeSlug: string; modelSlug?: string }) => data)
  .handler(async ({ data }): Promise<RelatedLinks> => {
    const { data: makeRow } = await supabase
      .from("makes")
      .select("id")
      .eq("slug", data.makeSlug)
      .single();

    let siblingModels: { name: string; slug: string }[] = [];
    if (makeRow) {
      const { data: models } = await supabase
        .from("models")
        .select("name, slug")
        .eq("make_id", makeRow.id)
        .order("name");
      const allModels = models ?? [];
      const modelStart = Math.max(
        allModels.findIndex((m) => m.slug === data.modelSlug),
        0,
      );
      siblingModels = Array.from(
        { length: allModels.length },
        (_, i) => allModels[(modelStart + i) % allModels.length],
      )
        .filter((m): m is { name: string; slug: string } => !!m && m.slug !== data.modelSlug)
        .slice(0, 15);
    }

    const { data: makes } = await supabase
      .from("makes")
      .select("name, slug")
      .order("name");
    // A fixed alphabetical slice always linked the same 18 brands, and
    // anchoring the rotation on the current make meant every page of a brand
    // emitted the identical neighbour list — niche makes (Lamborghini, etc.)
    // ended up with a single inbound link. Seeding the window with the full
    // page key spreads brand links across the whole catalogue.
    const allMakes = (makes ?? []).filter(
      (m): m is { name: string; slug: string } => !!m && m.slug !== data.makeSlug,
    );
    const otherMakes = rotatedWindow(
      allMakes,
      18,
      `${data.makeSlug}/${data.modelSlug ?? ""}`,
      (m) => m.slug,
    );


    return { siblingModels, otherMakes };
  });

export const getAllPageUrls = createServerFn({ method: "GET" }).handler(
  async () => {
    const { data: models, error } = await supabase
      .from("models")
      .select(
        `
        slug,
        makes!inner ( slug )
      `,
      );

    if (error) {
      console.error("getAllPageUrls error:", error);
      return { urls: [] as string[] };
    }

    const dataTypes = ALL_DATA_TYPE_SLUGS;
    const urls: string[] = [];

    for (const m of models ?? []) {
      const makeSlug = (m.makes as unknown as MakeRow).slug;
      for (const dt of dataTypes) {
        urls.push(`/${makeSlug}/${m.slug}/${dt}`);
      }
      urls.push(`/${makeSlug}/${m.slug}`);
    }

    // Add make pages
    const { data: makes } = await supabase.from("makes").select("slug");
    for (const mk of makes ?? []) {
      urls.push(`/${mk.slug}`);
    }

    // Money pages only exist where verified manufacturer specs are present
    const { data: specRows } = await supabase
      .from("vehicle_specs")
      .select("spec_type, models!inner ( slug, makes!inner ( slug ) )");
    const specUrls = new Set<string>();
    for (const r of specRows ?? []) {
      const m = r.models as unknown as { slug: string; makes: { slug: string } };
      specUrls.add(`/${m.makes.slug}/${m.slug}/${r.spec_type}`);
    }
    urls.push(...specUrls);

    return { urls };
  },
);
